import dayjs, { type Dayjs } from '@/config/dayjs';
import {
  API_DATE_FORMAT,
  APP_DATE_DISPLAY_FORMAT,
  APP_DATE_FORMAT,
} from '@/constants/dateFormats';

export {
  API_DATE_FORMAT,
  APP_DATE_DISPLAY_FORMAT,
  APP_DATE_FORMAT,
  APP_LONG_DATE_FORMAT,
  APP_MONTH_DAY_FORMAT,
} from '@/constants/dateFormats';

export type OptionalDateInput = string | number | Date | null | undefined;
export type OptionalDayjsInput = OptionalDateInput | Dayjs;

export interface FormatAppDateOptions {
  format?: string;
  fallback?: string;
  locale?: string;
}

/**
 * Strict counterpart to `parseAppDate`, for input nobody in the app produced —
 * URL parameters, stored values. Only the app and API formats are accepted, and
 * a calendar-invalid value such as `02/30/2026` is rejected rather than rolled
 * forward into March the way a lenient parse would.
 */
export const parseStrictAppDate = (
  value: string | null | undefined,
): Dayjs | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  const appFormat = dayjs(trimmed, APP_DATE_FORMAT, true);
  if (appFormat.isValid()) return appFormat;

  const apiFormat = dayjs(trimmed, API_DATE_FORMAT, true);
  return apiFormat.isValid() ? apiFormat : null;
};

/**
 * Parses a value as an app date (`MM/DD/YYYY`) when given a string; otherwise uses dayjs defaults.
 */
export const parseAppDate = (value: OptionalDayjsInput): Dayjs | null => {
  if (value == null) return null;

  const dateValue = typeof value === 'string' ? value.trim() : value;
  if (dateValue === '') return null;

  if (typeof dateValue === 'string') {
    const microsoftJsonDate = dateValue.match(
      /^\/Date\((-?\d+)(?:[+-]\d+)?\)\/$/,
    );
    if (microsoftJsonDate?.[1] !== undefined) {
      const timestamp = Number(microsoftJsonDate[1]);
      if (Number.isFinite(timestamp)) return dayjs(timestamp);
    }
    const strict = dayjs(dateValue, APP_DATE_FORMAT, true);
    if (strict.isValid()) return strict;
    const apiStrict = dayjs(dateValue, API_DATE_FORMAT, true);
    if (apiStrict.isValid()) return apiStrict;
  }

  const parsed = dayjs(dateValue);
  return parsed.isValid() ? parsed : null;
};

/**
 * Formats a date for display using the app display format by default.
 */
export const formatAppDate = (
  value: OptionalDayjsInput,
  options: FormatAppDateOptions = {},
): string => {
  const { format = APP_DATE_DISPLAY_FORMAT, fallback = '—', locale } = options;
  const parsed = parseAppDate(value);
  if (!parsed) return fallback;
  return locale ? parsed.locale(locale).format(format) : parsed.format(format);
};

/**
 * Formats a `Date` as `MM/DD/YYYY` for presets and controlled state strings.
 */
export const formatUsLocaleDate = (date: Date): string => {
  return dayjs(date).format(APP_DATE_FORMAT);
};

/**
 * Converts an app date string (`MM/DD/YYYY`) to API format (`YYYY-MM-DD`).
 */
export const toApiDate = (appDate: string): string => {
  const parsed = dayjs(appDate, APP_DATE_FORMAT, true);
  if (!parsed.isValid()) {
    throw new Error(`Invalid app date: ${appDate}`);
  }
  return parsed.format(API_DATE_FORMAT);
};

/**
 * Converts an app (`MM/DD/YYYY`) or API (`YYYY-MM-DD`) date string to API
 * format for query params. Returns the original string when it cannot be parsed.
 */
export const toApiDateParam = (value: string): string => {
  const parsed = parseAppDate(value);
  return parsed ? parsed.format(API_DATE_FORMAT) : value;
};

/**
 * Converts an API date string (`YYYY-MM-DD`) to app format (`MM/DD/YYYY`).
 */
export const fromApiDate = (apiDate: string): string => {
  const parsed = dayjs(apiDate, API_DATE_FORMAT, true);
  if (!parsed.isValid()) {
    throw new Error(`Invalid API date: ${apiDate}`);
  }
  return parsed.format(APP_DATE_FORMAT);
};

/** Formats a `Date` as `YYYY-MM-DD` for API query parameters. */
export const formatApiDateFromDate = (date: Date): string =>
  dayjs(date).format(API_DATE_FORMAT);

/**
 * Whether an as-of date refers to live values rather than a past snapshot.
 *
 * No date at all means today, and the API reads today's date live as well, so both count as
 * live. Anything else is a snapshot: its figures belong to another day, so they must not be
 * edited, written back, or patched with something that belongs to today.
 */
export const isLiveAsOfDate = (asOfDate?: string): boolean => {
  if (!asOfDate) return true;
  const parsed = parseAppDate(asOfDate);
  if (!parsed) return true;
  return parsed.isSame(dayjs(), 'day');
};

export const parseOptionalDate = (value: OptionalDateInput): Date | null => {
  const parsed = parseAppDate(value);
  return parsed ? parsed.toDate() : null;
};

/** @deprecated Use {@link formatAppDate} instead. */
export const formatDate = (date: Date) => formatAppDate(date);

/** @deprecated Use {@link formatAppDate} instead. */
export const formatOptionalDate = (
  value: OptionalDateInput,
  fallback = '—',
  locales?: Intl.LocalesArgument,
  options?: Intl.DateTimeFormatOptions,
) => {
  if (locales != null || options != null) {
    return (
      parseOptionalDate(value)?.toLocaleDateString(locales, options) ?? fallback
    );
  }
  return formatAppDate(value, { fallback });
};

/** @deprecated Use {@link parseAppDate} instead. */
export const parseOptionalDayjs = (value: OptionalDayjsInput): Dayjs | null =>
  parseAppDate(value);

/** @deprecated Use {@link formatAppDate} with an explicit `format` option instead. */
export const formatOptionalDayjs = (
  value: OptionalDayjsInput,
  format: string,
  fallback = '—',
) => formatAppDate(value, { format, fallback });

/**
 * Formats a Date using UTC calendar fields as `MM/DD/YYYY` (zero-padded month and day).
 *
 * Use for date-only API/query values and labels where local timezone must not change
 * the calendar day.
 */
export const formatUtcDate = (date: Date) => {
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  const monthText = String(month).padStart(2, '0');
  const dayText = String(day).padStart(2, '0');
  const yearText = String(year);

  return `${monthText}/${dayText}/${yearText}`;
};

/**
 * A day's calendar date as a UTC midnight `Date`, the input {@link formatUtcDate}
 * expects. Local midnight east of UTC is still the previous day in UTC, so a
 * local date passed straight through would be read a day early.
 */
export const toUtcCalendarDate = (day: Dayjs): Date =>
  new Date(Date.UTC(day.year(), day.month(), day.date()));
